import { useEffect, useState } from "react";

import * as Sentry from "@sentry/nextjs";
import { observer } from "mobx-react-lite";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useWindowSize } from "usehooks-ts";

import { disposeApi, getTeamCoursePreview, isApiError } from "@/api";
import * as Profile from "@/components/auth/Profile";
import { DocumentSkeleton } from "@/components/doc/DocumentSkeleton";
import { FormButton } from "@/components/form/FormButton";
import { useStore } from "@/store";
import { rememberPendingCourse } from "@/store/pendingCourse";
import { SCREEN_SM } from "@/style/breakpoints";
import { twMerge } from "@/utils/twMerge";

import type { TeamCoursePreview, TeamShort } from "@/api";

const AVATARS_LIMIT = { desktop: 5, mobile: 4 };

const CourseCard = observer(function CourseCard({
  team,
  course,
}: {
  team: TeamShort;
  course: TeamCoursePreview;
}) {
  const t = useTranslations();
  const { auth, signInFlow, studentOnboarding } = useStore();
  const pathname = usePathname();
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth < SCREEN_SM;
  const memberAvatars = course.memberAvatars.slice(
    0,
    isMobile ? AVATARS_LIMIT.mobile : AVATARS_LIMIT.desktop,
  );
  const hiddenMembersCount = Math.max(0, team.membersCount - memberAvatars.length);
  const avatarClassName = "h-9.5 w-9.5 rounded-full";

  const handleJoin = () => {
    if (!auth.isLoggedIn) {
      rememberPendingCourse(course);
      signInFlow.redirectToSignUp();
      return;
    }
    void studentOnboarding.joinCourseFromDocument(course, pathname);
  };

  return (
    <div className="relative flex flex-1 flex-col">
      <div className="pointer-events-none select-none blur-[3px]" aria-hidden>
        <DocumentSkeleton />
      </div>
      <div className="absolute inset-0 flex items-center justify-center font-geist">
        <div
          className={twMerge(
            "flex w-[330px] flex-col items-center rounded-12 bg-document-bg p-7.5 text-center",
            "shadow-block dark:shadow-block-inverted",
            "sm:w-[280px]",
          )}
        >
          <Profile.Userpic
            src={team.avatarUrls?.[120]}
            name={team.title || ""}
            setColorFrom={team.uid}
            className="w-25 h-25 text-[28px] sm:w-18 sm:h-18"
          />
          <h2 className="mt-1.5 text-[22px] font-semibold leading-none tracking-tight">
            {team.title}
          </h2>
          <p className="mt-1.5 text-size-15 text-gray-500">@{team.permalink}</p>
          <div className="mt-5 flex items-center gap-1.5">
            {memberAvatars.map((avatarUrls, index) => {
              const src = avatarUrls?.[48];
              return src ? (
                <img
                  key={src}
                  src={src}
                  alt={t("doc.coursePlaceholder.member")}
                  className={twMerge(avatarClassName, "object-cover")}
                />
              ) : (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  role="img"
                  aria-label={t("doc.coursePlaceholder.member")}
                  className={twMerge(avatarClassName, "bg-gray-500")}
                />
              );
            })}
            {hiddenMembersCount > 0 && (
              <span
                className={twMerge(
                  avatarClassName,
                  "flex items-center justify-center bg-document-bg text-size-16 text-gray-500 leading-none",
                  "border border-gray-200 dark:border-gray-800",
                )}
                aria-label={t("doc.coursePlaceholder.moreMembers", { count: hiddenMembersCount })}
              >
                {hiddenMembersCount}
              </span>
            )}
          </div>
          <FormButton
            type="button"
            variant="accent"
            className="mt-5 w-full h-8.5 text-[15px] tracking-tight rounded-lg"
            onClick={handleJoin}
            isBusy={studentOnboarding.isJoiningCourses}
          >
            {t("doc.coursePlaceholder.joinClass")}
          </FormButton>
          {studentOnboarding.joinFailureMessageKey && (
            <p className="mt-2 text-xs text-red-500">
              {t(studentOnboarding.joinFailureMessageKey)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
});

export const DocPlaceholder = observer(function DocPlaceholder({
  team,
}: {
  team?: TeamShort | null;
}) {
  const { teams } = useStore();
  const [course, setCourse] = useState<TeamCoursePreview | null>(null);
  const isMember = !!team && teams.userTeams.some(({ uid }) => uid === team.uid);
  const permalink = team && !team.personal && !isMember ? team.permalink : undefined;

  useEffect(() => {
    if (!permalink) return undefined;

    return disposeApi(async (signal) => {
      try {
        const { coursePreview } = await getTeamCoursePreview(permalink, { signal });
        setCourse(coursePreview);
      } catch (err) {
        if (!(isApiError(err) && err.response?.status === 404)) {
          Sentry.captureException(err);
        }
      }
    });
  }, [permalink]);

  if (team && course && !isMember) {
    return <CourseCard team={team} course={course} />;
  }

  return <DocumentSkeleton />;
});
